import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Box,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  Tooltip,
  Switch,
  FormControlLabel,
  Alert
} from "@mui/material";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import HistoryIcon from "@mui/icons-material/History";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { getApiBaseUrl } from "../api";

const UNIT_OPTIONS = ['бр.', 'компл.', 'л', 'кг', 'м', 'м²', 'рол.', 'опаковка'];

const formatNumber = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return Number.isInteger(n)
    ? n.toLocaleString('bg-BG')
    : n.toLocaleString('bg-BG', { maximumFractionDigits: 3 });
};

const getStockChip = (part, t) => {
  const qty = Number(part?.quantity ?? 0);
  const min = Number(part?.min_quantity ?? 0);
  if (qty <= 0) {
    return { label: t('outOfStock'), color: 'error' };
  }
  if (qty <= min) {
    return { label: t('lowStock'), color: 'warning' };
  }
  return { label: t('inStock'), color: 'success' };
};

export default function Parts({ t, userRole, userPermissions }) {
  const isAdmin = String(userRole || '').trim() === 'admin';
  const perm = useMemo(
    () => (Array.isArray(userPermissions) ? userPermissions.find((p) => p.module === 'parts') : null),
    [userPermissions]
  );
  const canWrite = isAdmin || (perm && Number(perm.can_write) === 1);
  const canDelete = isAdmin || (perm && Number(perm.can_delete) === 1);

  const fullScreenDialog = useMediaQuery('(max-width:600px)');

  const [parts, setParts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Add / Edit form dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [draft, setDraft] = useState({
    name: '',
    sku: '',
    category: '',
    unit: 'бр.',
    quantity: '',
    min_quantity: '',
    price: '',
    location: '',
    notes: '',
  });

  // Stock movement dialog (приход/разход)
  const [movementOpen, setMovementOpen] = useState(false);
  const [movementPart, setMovementPart] = useState(null);
  const [movementSaving, setMovementSaving] = useState(false);
  const [movementError, setMovementError] = useState('');
  const [movementDraft, setMovementDraft] = useState({
    type: 'in',
    quantity: '',
    unit_price: '',
    supplier: '',
    notes: '',
  });

  // Movement history dialog
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyPart, setHistoryPart] = useState(null);
  const [movements, setMovements] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Delete confirm dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePart, setDeletePart] = useState(null);
  const [deleteSaving, setDeleteSaving] = useState(false);

  const loadParts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${getApiBaseUrl()}/parts`);
      setParts(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e?.response?.data?.error || t('partsLoadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${getApiBaseUrl()}/parts/categories`);
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch {
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    loadParts();
    loadCategories();
  }, [loadParts, loadCategories]);

  const filteredParts = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    return (parts || []).filter((p) => {
      if (categoryFilter && String(p?.category || '') !== categoryFilter) return false;
      if (lowStockOnly && Number(p?.quantity ?? 0) > Number(p?.min_quantity ?? 0)) return false;
      if (!q) return true;
      return [p?.name, p?.sku, p?.category, p?.location, p?.notes]
        .some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [parts, search, categoryFilter, lowStockOnly]);

  const openAddForm = () => {
    setEditingPart(null);
    setDraft({
      name: '',
      sku: '',
      category: '',
      unit: 'бр.',
      quantity: '',
      min_quantity: '',
      price: '',
      location: '',
      notes: '',
    });
    setFormError('');
    setFormOpen(true);
  };

  const openEditForm = (part) => {
    setEditingPart(part);
    setDraft({
      name: part?.name || '',
      sku: part?.sku || '',
      category: part?.category || '',
      unit: part?.unit || 'бр.',
      quantity: part?.quantity ?? '',
      min_quantity: part?.min_quantity ?? '',
      price: part?.price ?? '',
      location: part?.location || '',
      notes: part?.notes || '',
    });
    setFormError('');
    setFormOpen(true);
  };

  const handleSavePart = async () => {
    if (!String(draft.name || '').trim()) {
      setFormError(t('partNameRequired'));
      return;
    }

    const payload = {
      name: String(draft.name).trim(),
      sku: String(draft.sku).trim(),
      category: String(draft.category).trim(),
      unit: String(draft.unit).trim() || 'бр.',
      min_quantity: String(draft.min_quantity).replace(',', '.'),
      price: String(draft.price).replace(',', '.'),
      location: String(draft.location).trim(),
      notes: String(draft.notes).trim(),
    };

    // Initial stock is only set on create (after that it is tracked via movements)
    if (!editingPart) {
      payload.quantity = String(draft.quantity).replace(',', '.') || 0;
    }

    setFormSaving(true);
    setFormError('');
    try {
      if (editingPart) {
        await axios.put(`${getApiBaseUrl()}/parts/${editingPart.id}`, payload);
      } else {
        await axios.post(`${getApiBaseUrl()}/parts`, payload);
      }
      setFormOpen(false);
      await Promise.all([loadParts(), loadCategories()]);
    } catch (e) {
      setFormError(e?.response?.data?.error || t('partsSaveError'));
    } finally {
      setFormSaving(false);
    }
  };

  const openMovement = (part) => {
    setMovementPart(part);
    setMovementDraft({
      type: 'in',
      quantity: '',
      unit_price: part?.price != null && part?.price !== '' ? String(part.price) : '',
      supplier: '',
      notes: '',
    });
    setMovementError('');
    setMovementOpen(true);
  };

  const handleMovementSubmit = async () => {
    const qty = Number(String(movementDraft.quantity || '').replace(',', '.'));
    if (!Number.isFinite(qty) || qty <= 0) {
      setMovementError(t('quantityPositive'));
      return;
    }

    setMovementSaving(true);
    setMovementError('');
    try {
      const res = await axios.post(`${getApiBaseUrl()}/parts/${movementPart.id}/movements`, {
        type: movementDraft.type,
        quantity: qty,
        unit_price: String(movementDraft.unit_price || '').replace(',', '.') || null,
        supplier: String(movementDraft.supplier).trim(),
        notes: String(movementDraft.notes).trim(),
      });
      setMovementOpen(false);
      setParts((prev) => prev.map((p) => (p.id === movementPart.id ? res.data.part : p)));
      if (historyOpen && historyPart?.id === movementPart.id) {
        await loadMovements(movementPart.id);
      }
    } catch (e) {
      setMovementError(e?.response?.data?.error || t('partsAdjustError'));
    } finally {
      setMovementSaving(false);
    }
  };

  const loadMovements = async (partId) => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${getApiBaseUrl()}/parts/${partId}/movements`);
      setMovements(Array.isArray(res.data) ? res.data : []);
    } catch {
      setMovements([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistory = (part) => {
    setHistoryPart(part);
    setMovements([]);
    setHistoryOpen(true);
    loadMovements(part.id);
  };

  const openDelete = (part) => {
    setDeletePart(part);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    setDeleteSaving(true);
    try {
      await axios.delete(`${getApiBaseUrl()}/parts/${deletePart.id}`);
      setDeleteOpen(false);
      setParts((prev) => prev.filter((p) => p.id !== deletePart.id));
      await loadCategories();
    } catch (e) {
      setError(e?.response?.data?.error || t('partsDeleteError'));
      setDeleteOpen(false);
    } finally {
      setDeleteSaving(false);
    }
  };

  const unitOptions = useMemo(() => {
    const set = new Set(UNIT_OPTIONS);
    if (draft.unit && !set.has(draft.unit)) set.add(draft.unit);
    return [...set];
  }, [draft.unit]);

  const isFormQuantityDisabled = Boolean(editingPart);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Inventory2Icon />
        {t('partsTitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3, p: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label={t('partsSearch')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('partsSearchPlaceholder')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('category')}</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label={t('category')}
              >
                <MenuItem value="">{t('allCategories')}</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={lowStockOnly}
                  onChange={(e) => setLowStockOnly(e.target.checked)}
                  color="warning"
                />
              }
              label={t('lowStockOnly')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            {canWrite && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={openAddForm}>
                {t('addPart')}
              </Button>
            )}
          </Grid>
        </Grid>
      </Card>

      {/* Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">
              {t('results')} ({filteredParts.length})
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('showItems')}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select value={itemsPerPage} onChange={(e) => setItemsPerPage(e.target.value)}>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {loading ? (
            <Typography align="center" sx={{ py: 4 }} color="text.secondary">
              {t('loading')}...
            </Typography>
          ) : (
            <>
              {/* Desktop table */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>{t('partName')}</strong></TableCell>
                        <TableCell><strong>{t('sku')}</strong></TableCell>
                        <TableCell><strong>{t('category')}</strong></TableCell>
                        <TableCell align="center"><strong>{t('quantity')}</strong></TableCell>
                        <TableCell align="center"><strong>{t('minQuantity')}</strong></TableCell>
                        <TableCell align="right"><strong>{t('price')}</strong></TableCell>
                        <TableCell><strong>{t('location')}</strong></TableCell>
                        <TableCell align="right"><strong>{t('actions')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredParts.slice(0, itemsPerPage).map((part) => {
                        const chip = getStockChip(part, t);
                        return (
                          <TableRow key={part.id} hover>
                            <TableCell>
                              <Typography variant="body1" fontWeight="bold">
                                {part.name}
                              </Typography>
                              {part.notes ? (
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 220 }}>
                                  {part.notes}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell>{part.sku || '-'}</TableCell>
                            <TableCell>
                              {part.category ? <Chip label={part.category} size="small" variant="outlined" /> : '-'}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                size="small"
                                color={chip.color}
                                label={`${formatNumber(part.quantity)} ${part.unit || ''}`}
                                sx={{ fontWeight: 700 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {formatNumber(part.min_quantity)} {part.unit || ''}
                            </TableCell>
                            <TableCell align="right">
                              {part.price != null && part.price !== '' ? `${formatNumber(part.price)} лв` : '-'}
                            </TableCell>
                            <TableCell>{part.location || '-'}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                {canWrite && (
                                  <Tooltip title={t('adjustStock')}>
                                    <IconButton size="small" color="primary" onClick={() => openMovement(part)}>
                                      <SwapHorizIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title={t('movementHistory')}>
                                  <IconButton size="small" color="info" onClick={() => openHistory(part)}>
                                    <HistoryIcon />
                                  </IconButton>
                                </Tooltip>
                                {canWrite && (
                                  <Tooltip title={t('edit')}>
                                    <IconButton size="small" color="default" onClick={() => openEditForm(part)}>
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {canDelete && (
                                  <Tooltip title={t('delete')}>
                                    <IconButton size="small" color="error" onClick={() => openDelete(part)}>
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {filteredParts.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            {t('noPartsFound')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Mobile cards */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <Grid container spacing={2}>
                  {filteredParts.slice(0, itemsPerPage).map((part) => {
                    const chip = getStockChip(part, t);
                    return (
                      <Grid item xs={12} key={part.id}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {part.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {[part.sku, part.category].filter(Boolean).join(' • ') || '-'}
                                </Typography>
                              </Box>
                              <Chip size="small" color={chip.color} label={`${formatNumber(part.quantity)} ${part.unit || ''}`} sx={{ fontWeight: 700 }} />
                            </Box>
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              <Chip size="small" variant="outlined" label={`${t('minQuantity')}: ${formatNumber(part.min_quantity)} ${part.unit || ''}`} />
                              {part.price != null && part.price !== '' && (
                                <Chip size="small" variant="outlined" label={`${formatNumber(part.price)} лв`} />
                              )}
                              {part.location && <Chip size="small" variant="outlined" label={part.location} />}
                            </Box>
                            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {canWrite && (
                                <Button size="small" variant="outlined" color="primary" startIcon={<SwapHorizIcon />} onClick={() => openMovement(part)}>
                                  {t('adjustStock')}
                                </Button>
                              )}
                              <Button size="small" variant="outlined" startIcon={<HistoryIcon />} onClick={() => openHistory(part)}>
                                {t('movementHistory')}
                              </Button>
                              {canWrite && (
                                <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEditForm(part)}>
                                  {t('edit')}
                                </Button>
                              )}
                              {canDelete && (
                                <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => openDelete(part)}>
                                  {t('delete')}
                                </Button>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                  {filteredParts.length === 0 && (
                    <Grid item xs={12}>
                      <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                        {t('noPartsFound')}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog
        open={formOpen}
        onClose={() => !formSaving && setFormOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>
          {editingPart ? t('editPart') : t('addPart')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label={t('partName')}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('sku')}
                value={draft.sku}
                onChange={(e) => setDraft({ ...draft, sku: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('category')}
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('unit')}</InputLabel>
                <Select
                  value={draft.unit}
                  onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
                  label={t('unit')}
                >
                  {unitOptions.map((u) => (
                    <MenuItem key={u} value={u}>{u}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={editingPart ? `${t('quantity')} (${formatNumber(editingPart.quantity)} ${editingPart.unit || ''})` : t('quantity')}
                value={draft.quantity}
                disabled={isFormQuantityDisabled}
                helperText={editingPart ? t('quantityEditHint') : ''}
                onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('minQuantity')}
                value={draft.min_quantity}
                onChange={(e) => setDraft({ ...draft, min_quantity: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('price')}
                value={draft.price}
                onChange={(e) => setDraft({ ...draft, price: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('location')}
                value={draft.location}
                onChange={(e) => setDraft({ ...draft, location: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label={t('notes')}
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              />
            </Grid>
          </Grid>
          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>{formError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={formSaving}>{t('cancel')}</Button>
          <Button variant="contained" onClick={handleSavePart} disabled={formSaving}>
            {formSaving ? t('saving') : t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stock movement dialog */}
      <Dialog
        open={movementOpen}
        onClose={() => !movementSaving && setMovementOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>
          {movementPart ? `${t('adjustStock')} — ${movementPart.name}` : t('adjustStock')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {movementPart && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('currentStock')}: <strong>{formatNumber(movementPart.quantity)} {movementPart.unit || ''}</strong>
            </Typography>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('movementType')}</InputLabel>
                <Select
                  value={movementDraft.type}
                  onChange={(e) => setMovementDraft({ ...movementDraft, type: e.target.value })}
                  label={t('movementType')}
                >
                  <MenuItem value="in">{t('stockIn')}</MenuItem>
                  <MenuItem value="out">{t('stockOut')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={t('movementQuantity')}
                value={movementDraft.quantity}
                onChange={(e) => setMovementDraft({ ...movementDraft, quantity: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('price')}
                value={movementDraft.unit_price}
                onChange={(e) => setMovementDraft({ ...movementDraft, unit_price: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('supplier')}
                value={movementDraft.supplier}
                onChange={(e) => setMovementDraft({ ...movementDraft, supplier: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label={t('notes')}
                value={movementDraft.notes}
                onChange={(e) => setMovementDraft({ ...movementDraft, notes: e.target.value })}
              />
            </Grid>
          </Grid>
          {movementError && (
            <Alert severity="error" sx={{ mt: 2 }}>{movementError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMovementOpen(false)} disabled={movementSaving}>{t('cancel')}</Button>
          <Button
            variant="contained"
            color={movementDraft.type === 'out' ? 'error' : 'success'}
            startIcon={movementDraft.type === 'out' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
            onClick={handleMovementSubmit}
            disabled={movementSaving}
          >
            {movementSaving ? t('saving') : (movementDraft.type === 'out' ? t('stockOut') : t('stockIn'))}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Movement history dialog */}
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>
          {historyPart ? `${t('movementHistory')} — ${historyPart.name}` : t('movementHistory')}
        </DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Typography align="center" sx={{ py: 4 }} color="text.secondary">
              {t('loading')}...
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{t('movementType')}</strong></TableCell>
                    <TableCell align="right"><strong>{t('movementQuantity')}</strong></TableCell>
                    <TableCell align="right"><strong>{t('price')}</strong></TableCell>
                    <TableCell><strong>{t('supplier')}</strong></TableCell>
                    <TableCell><strong>{t('notes')}</strong></TableCell>
                    <TableCell><strong>{t('movementDate')}</strong></TableCell>
                    <TableCell><strong>{t('movementBy')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movements.map((m) => (
                    <TableRow key={m.id} hover>
                      <TableCell>
                        <Chip
                          size="small"
                          color={m.type === 'in' ? 'success' : 'error'}
                          icon={m.type === 'in' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
                          label={m.type === 'in' ? t('stockIn') : t('stockOut')}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <strong style={{ color: m.type === 'in' ? '#2e7d32' : '#d32f2f' }}>
                          {m.type === 'in' ? '+' : '-'}{formatNumber(m.quantity)}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        {m.unit_price != null && m.unit_price !== '' ? `${formatNumber(m.unit_price)} лв` : '-'}
                      </TableCell>
                      <TableCell>{m.supplier || '-'}</TableCell>
                      <TableCell>{m.notes || '-'}</TableCell>
                      <TableCell>{m.created_at ? String(m.created_at).replace('T', ' ').slice(0, 16) : '-'}</TableCell>
                      <TableCell>{m.user_nickname || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {movements.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        {t('noMovements')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>{t('close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={deleteOpen} onClose={() => !deleteSaving && setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('deletePartConfirm')}
          </Typography>
          {deletePart && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <strong>{deletePart.name}</strong>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteSaving}>{t('cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleteSaving}>
            {deleteSaving ? t('saving') : t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
